import {
  isIframe,
  sendPostMessageIframe,
  SDK__ON_FINISH,
} from '../iframeBridge/iframeBridge';

export const IFRAME_TERMINAL_DISPLAY_MS = 2000;

export const IFRAME_NAVIGATE_AFTER_FINISH_MS = 2000;

const ANONYMOUS_PROCESS_KEY = '__anonymous__';

// FINISH is a terminal, once-per-process event. Callers can reach closeFlow
// through several paths (timer expiration, navigation effects, polling success),
// and each call used to schedule its own postMessage, so integrators received
// onFinish several times for a single journey. Keyed by processId so a genuinely
// new process in the same page load can still notify.
const _notifiedProcesses = new Set();

const _terminalKey = (processId) => processId || ANONYMOUS_PROCESS_KEY;

export const hasNotifiedTerminal = (processId) =>
  _notifiedProcesses.has(_terminalKey(processId));

export const __resetTerminalNotifications = () => {
  _notifiedProcesses.clear();
};

export const notifyTerminal = ({
  isCompleted,
  redirectUrl,
  processId,
  token,
  onFinalize,
  onNavigate,
  deps = {},
} = {}) => {
  const _isIframe = deps.isIframe || isIframe;
  const _postMessage = deps.sendPostMessageIframe || sendPostMessageIframe;
  const _setTimeout = deps.setTimeout || setTimeout;
  const _clearTimeout = deps.clearTimeout || clearTimeout;

  const navigate = () => {
    if (typeof onNavigate === 'function') onNavigate();
  };

  if (redirectUrl && !_isIframe()) {
    navigate();
    window.location.href = redirectUrl;
    return undefined;
  }

  if (isCompleted && !redirectUrl && !_isIframe()) {
    navigate();
    return undefined;
  }

  const finalize = () => {
    if (typeof onFinalize === 'function') onFinalize();
    _postMessage(SDK__ON_FINISH, {
      transaction: {
        id: processId,
        token: token ? token.slice(-10) : undefined,
        concluded: isCompleted,
      },
    });
  };

  const key = _terminalKey(processId);

  if (_isIframe()) {
    // Claim the process at schedule time, not at finalize time: duplicated
    // calls arrive before the 2s delay elapses, so a late claim would still
    // let every one of them schedule its own postMessage.
    if (_notifiedProcesses.has(key)) return undefined;
    _notifiedProcesses.add(key);

    let navigateId;
    let finalized = false;

    const finishId = _setTimeout(() => {
      finalized = true;
      finalize();
      navigateId = _setTimeout(navigate, IFRAME_NAVIGATE_AFTER_FINISH_MS);
    }, IFRAME_TERMINAL_DISPLAY_MS);

    return () => {
      _clearTimeout(finishId);
      if (navigateId) _clearTimeout(navigateId);
      // Releasing the claim only makes sense while nothing was sent yet;
      // after finalize the process is terminally notified.
      if (!finalized) _notifiedProcesses.delete(key);
    };
  }

  navigate();

  if (!_notifiedProcesses.has(key)) {
    _notifiedProcesses.add(key);
    finalize();
  }

  return undefined;
};
